import { Box, Button, Tooltip } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { IntlShape } from 'react-intl';
import {TimeSheetItemStatus, UpdateStatusAbility} from '../../../features/schedule/types';
import styles from './time-shift.module.scss'
import classNames from 'classnames';
import StateIcon from '../timesheet-state-icon';
import ToolActionMenu from './components/action-menu';
import { Lx } from '../../../i18n/consts';

interface Props {
    intl: IntlShape;
    totalHours: number;
    approved: number;
    billed: number;
    approvable: number;
    billable: number;
    approveAbility?: UpdateStatusAbility,
    billalbeAbility?: UpdateStatusAbility
    onAddNewShiftClick?: () => Promise<void>;
    onCancelBillClick?: () => Promise<void>;
    onBillClick?: () => Promise<void>;
    onDisapproveClick?: () => Promise<void>;
    onApproveClick?: () => Promise<void>;
    totalWorkHours?: number;
    hideButtons?: boolean;
    plannedHours?: number;
    hideHourSuffix?: boolean;
}

const TimeSheetGroupTool: React.FC<Props> = (props) => {
    const { totalHours, totalWorkHours, intl, approved, approvable, billed, billable, approveAbility, billalbeAbility } = props;
    const [statusChanging, setStatusChanging] = React.useState<boolean>(false);
    const [state, setState] = useState<{ isApproved: boolean, isBillable: boolean }>({ isApproved: false, isBillable: false });
    const [approveTooltipOpen, setApproveTooltipOpen] = useState(false);
    const [billableTooltipOpen, setBillableTooltipOpen] = useState(false);
    const totalDigits = props.totalHours.toString().length;
    const hiddenApproveButtons = totalHours < 1 || (!!approveAbility && !approveAbility.canApply && !approveAbility.canCancel);
    const hiddenBillableButtons = !totalWorkHours || totalWorkHours < 1 || (!!billalbeAbility && !billalbeAbility.canApply && !billalbeAbility.canCancel);
    const showPlanned = props.hideButtons && props.plannedHours !== undefined;

    const approveTooltipTitle = approvable > 0 ? intl.formatMessage({ id: Lx.Schedule.APPROVE_ALL_FORMAT }, { s: approvable }) : intl.formatMessage({ id: Lx.Schedule.DISAPPROVE_FORMAT_ALL }, { s: approved });
    const billTooltipTitle = billable > 0 ? intl.formatMessage({ id: Lx.Schedule.BILL_ALL_FORMAT}, { s: billable }) : intl.formatMessage({ id: Lx.Schedule.CANCEL_BILL_FORMAT }, { s: billed });

    const handleApproveButtonClick = () => {
        setStatusChanging(true);
        handleCloseApproveTooltip();
        if (state.isApproved) {
            props.onDisapproveClick?.().finally(() => setStatusChanging(false));
            return;
        }

        props.onApproveClick?.().finally(() => setStatusChanging(false));
    }

    const handleBillButtonClick = () => {
        setStatusChanging(true);
        handleCloseApproveTooltip();
        if (state.isBillable) {
            props.onCancelBillClick?.().finally(() => setStatusChanging(false));
            return;
        }

        props.onBillClick?.().finally(() => setStatusChanging(false));
    }
    const isApproveButtonDisabled = () => statusChanging || (approveAbility ? state.isApproved ? !approveAbility.canCancel : !approveAbility.canApply : false);
    const isBillableButtonDisabled = () => statusChanging || (billalbeAbility ? state.isBillable ? !billalbeAbility.canCancel : !billalbeAbility.canApply : false);
    const handleOpenApproveTooltip = () => statusChanging ? void(0) : setApproveTooltipOpen(true);
    const handleCloseApproveTooltip = () => setApproveTooltipOpen(false);
    const handleOpenBillableTooltip = () => statusChanging ? void(0) : setBillableTooltipOpen(true);
    const handleCloseBillableTooltip = () => setBillableTooltipOpen(false);

    useEffect(() => {
        setState({
            isApproved: totalHours > 0 && approved === totalHours,
            isBillable: !hiddenBillableButtons && billed === totalWorkHours
        });
    }, [approved, billed])

    const containerClassName = classNames({
        [styles.container]: true,
        [styles.small]: props.hideButtons !== undefined && props.hideButtons,
    });

    return(<Box className={containerClassName}>
        <div className={classNames({
            [styles.col]: true,
            [styles.small]: totalDigits > 4 && !props.hideButtons,
            [styles.colCompact]: props.hideButtons,
        })}>
            <div className={styles.hours}>{props.totalHours}{props.hideHourSuffix ? '' : ' h'}</div>
            {
                showPlanned && (
                    <div className={styles.planned}>
                        <div className={styles.plannedDivider} />
                        <span className={styles.plannedValue}>
                            {Math.round((props.plannedHours ?? 0) * 10) / 10}
                        </span>
                    </div>
                )
            }
        </div>
        {
            !props.hideButtons && <Box className={classNames({
                [styles.menu]: true
            })}>
                {
                    <Tooltip title={approveTooltipTitle}
                        open={approveTooltipOpen} 
                        onClose={handleCloseApproveTooltip} 
                        onOpen={handleOpenApproveTooltip}>
                        <span>
                            <Button className={classNames({
                                [styles.state]: true,
                                [styles.approved]: state.isApproved
                            })} disabled={hiddenApproveButtons || isApproveButtonDisabled()}
                            onClick={handleApproveButtonClick}>
                                <StateIcon

                                    status={state.isApproved ? TimeSheetItemStatus.APPROVED : TimeSheetItemStatus.NORMAL}
                                    active={state.isApproved}/>
                            </Button>
                        </span>
                    </Tooltip>
                }
                {
                    <Tooltip title={billTooltipTitle}
                        open={billableTooltipOpen}
                        onClick={handleOpenBillableTooltip}
                        onClose={handleCloseBillableTooltip}>
                        <span>
                            <Button className={classNames({
                                [styles.state]: true,
                                [styles.billable]: state.isBillable,
                                [styles.notAvail]: !state.isBillable
                            })} disabled={hiddenBillableButtons|| isBillableButtonDisabled()}  onClick={handleBillButtonClick}>
                                <StateIcon status={TimeSheetItemStatus.BILLABLE}
                                    active={state.isBillable}/>
                            </Button>
                        </span>
                    </Tooltip>
                }
                <ToolActionMenu intl={intl}
                    state={state}
                    approvedHours={approved}
                    approvableHours={approvable}
                    billedHours={billed}
                    billableHours={billable}
                    onAddNewShiftClick={props.onAddNewShiftClick}
                    onApproveClick={props.onApproveClick}
                    onDisaproveClick={props.onDisapproveClick}
                    onBillClick={props.onBillClick}
                    onCancelBillClick={props.onCancelBillClick}
                    hideBillable={hiddenBillableButtons}
                    hideApprove={hiddenApproveButtons}
                    approveAbility={approveAbility}
                    billableAbility={billalbeAbility}
                    statusChanging={statusChanging}/>
            </Box>
        }
    </Box>)
}

export default TimeSheetGroupTool;
